import { projects } from '@/mocks/projects';
import { absoluteUrl, SITE_NAME, SITE_ORIGIN, DEFAULT_OG_IMAGE } from './siteMeta';

export type StructuredData = Record<string, unknown>;

const EMAIL = 'contacto@amestudio.mx';
const PHONE = '+524444177181';

const organization = {
  '@type': 'Organization',
  name: SITE_NAME,
  url: `${SITE_ORIGIN}/`,
  email: EMAIL,
  telephone: PHONE,
};

function homeSchema(): StructuredData {
  return {
    '@context': 'https://schema.org',
    '@type': 'ProfessionalService',
    name: SITE_NAME,
    description:
      'Estudio de arquitectura, diseño y construcción en San Luis Potosí, México.',
    url: `${SITE_ORIGIN}/`,
    image: DEFAULT_OG_IMAGE,
    areaServed: [
      'San Luis Potosí',
      'Lomas del Tecnológico',
      'Villa Magna',
      'Sierra Azul',
      'Soledad de Graciano Sánchez',
    ],
    telephone: PHONE,
    email: EMAIL,
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'San Luis Potosí',
      addressRegion: 'San Luis Potosí',
      addressCountry: 'MX',
    },
    openingHoursSpecification: {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '09:00',
      closes: '18:00',
    },
  };
}

function projectsSchema(): StructuredData {
  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Proyectos',
    description:
      'Portafolio de proyectos de diseño arquitectónico, interiores y espacios comerciales de AM ESTUDIO en San Luis Potosí.',
    url: absoluteUrl('/proyectos'),
    isPartOf: {
      '@type': 'WebSite',
      name: SITE_NAME,
      url: `${SITE_ORIGIN}/`,
    },
    hasPart: projects.map((p) => ({
      '@type': 'CreativeWork',
      name: p.name,
      url: absoluteUrl(`/proyectos/${p.slug}`),
    })),
  };
}

function projectSchema(slug: string): StructuredData | null {
  const project = projects.find((p) => p.slug === slug);
  if (!project) return null;

  const gallery = (project as { gallery?: string[] }).gallery ?? [];

  return {
    '@context': 'https://schema.org',
    '@type': 'CreativeWork',
    name: project.name,
    description: project.description,
    url: absoluteUrl(`/proyectos/${project.slug}`),
    image: [project.image, ...gallery],
    dateCreated: project.year,
    genre: project.category,
    creator: organization,
    locationCreated: {
      '@type': 'Place',
      name: `${project.zone}, San Luis Potosí, México`,
    },
  };
}

export function resolveStructuredData(pathname: string): StructuredData[] {
  const clean =
    pathname.split('?')[0].split('#')[0].replace(/\/+$/, '') || '/';

  if (clean === '/') return [homeSchema()];
  if (clean === '/proyectos') return [projectsSchema()];

  const match = clean.match(/^\/proyectos\/([^/]+)$/);
  if (match) {
    const schema = projectSchema(decodeURIComponent(match[1]));
    return schema ? [schema] : [];
  }

  // 404 / unknown routes are not indexable, so no structured data is emitted.
  return [];
}